import { Card, makeStyles, Theme } from "@material-ui/core";
import { Warning } from "@material-ui/icons";
import { useEffect, useState } from "react";
import {
  DateField,
  FunctionField,
  Labeled,
  NumberField,
  Record,
  RecordContextProvider,
  ReferenceField,
  SelectField,
  TextField,
  useTranslate,
} from "react-admin";
import { handleAxiosError } from "../../../common/data/axiosErrorHandler";
import resourceNames from "../../../common/data/resourceNames";
import {
  EventLocationSource,
  MasterEventApiFactory,
  UpdateMasterEventInput,
} from "../../../common/data/service-proxies";
import { useAxiosInstance } from "../../../common/hooks/useAxiosInstance";
import { IDropDownAppData } from "../../../common/redux/reducers/appDataReducer";
import { EventSequenceNumberField } from "./EventSequenceNumberField";
import { useForm } from "react-final-form";
import classes from "./FormInformation.module.css";

interface Props {
  eventTypeCodes: Array<IDropDownAppData<string>>;
  startTime?: string;
  homeTerminalTimeZone?: string;
  driverId?: number;
  record?: any;
  variant?: any;
  margin?: any;
}

const useStyles = makeStyles<Theme>((x) => ({
  error: {
    color: x.palette.warning.main,
  },
  fieldset: {
    borderColor: x.palette.divider,
  },
}));

export const FormInformation = (props: Props) => {
  const {
    startTime,
    homeTerminalTimeZone,
    driverId,
    record,
    eventTypeCodes,
  } = props;
  const axios = useAxiosInstance();

  const translate = useTranslate();
  const [info, setInfo] = useState<UpdateMasterEventInput>();
  const [error, setError] = useState<boolean>(false);
  const styles = useStyles();
  const form = useForm();

  const [eventStartTime, setEventStartTime] = useState<string>(
    startTime ?? record.date
  );

  useEffect(() => {
    if (!startTime) {
      return;
    }

    setEventStartTime(startTime);
  }, [startTime]);

  useEffect(() => {
    if (!axios || !form) {
      return;
    }

    if (!eventStartTime || !driverId) {
      return;
    }

    MasterEventApiFactory(undefined, axios.defaults.baseURL, axios)
      .apiAppMastereventPreviouseventDriverIdGet(driverId, eventStartTime)
      .then((response) => {
        setInfo(response.data);
        const trailerNumbers = form.getFieldState("trailerNumbers");
        if (!trailerNumbers?.dirty && !trailerNumbers?.touched) {
          trailerNumbers?.change(response.data.trailerNumbers);
        }

        const shippingDocumentNumber = form.getFieldState(
          "shippingDocumentNumber"
        );
        if (
          !shippingDocumentNumber?.dirty &&
          !shippingDocumentNumber?.touched
        ) {
          shippingDocumentNumber?.change(response.data.shippingDocumentNumber);
        }

        setError(false);
      })
      .catch((e) => {
        console.error(handleAxiosError(e));
        setError(true);
      });
  }, [axios, driverId, eventStartTime, form]);

  return (
    <div className={classes["form-information-container"]}>
      <RecordContextProvider value={info}>
        <Card className="form-information-container-inner">
          <fieldset className={styles.fieldset}>
            <legend>Previous event</legend>
            {error ? (
              <div className={classes.error}>
                <Warning className={styles.error} />
                <span>Unable to get the previous event info</span>
              </div>
            ) : (
              <>
                <div>
                  <Labeled label="Start time">
                    <DateField
                      source="startTime"
                      showTime
                      options={{ timeZone: homeTerminalTimeZone }}
                    />
                  </Labeled>
                </div>
                <div>
                  <Labeled label="Status event type">
                    {eventTypeCodes.some(
                      (x) => x.id === info?.eventTypeCode
                    ) ? (
                      <SelectField
                        source="eventTypeCode"
                        choices={eventTypeCodes}
                      />
                    ) : (
                      <FunctionField<Record>
                        source="eventTypeCode"
                        render={(record) => (
                          <span>
                            {translate(
                              `appData.eventTypeCodes.${record?.eventTypeCode}`,
                              { _: record?.eventTypeCode }
                            )}
                          </span>
                        )}
                      />
                    )}
                  </Labeled>
                </div>
                <div className="two-inputs-container">
                  <div>
                    <Labeled label="Location specified">
                      <SelectField
                        source="locationSource"
                        choices={[
                          {
                            id: EventLocationSource.SelectedFromMap,
                            name: "By coordinates",
                          },
                          {
                            id: EventLocationSource.ManualInput,
                            name: "By describing it manually",
                          },
                        ]}
                      />
                    </Labeled>
                  </div>
                  <div>
                    <Labeled label="Geolocation">
                      {info?.locationSource ===
                      EventLocationSource.ManualInput ? (
                        <TextField source="driversLocationDescription" />
                      ) : (
                        <TextField source="geolocation" />
                      )}
                    </Labeled>
                  </div>
                </div>
                <div className="two-inputs-container">
                  <div>
                    <Labeled label="Vehicle">
                      <ReferenceField
                        reference={resourceNames.ADMIN_VEHICLES_VIEW}
                        source="vehicleId"
                        link={false}
                      >
                        <TextField source="name" />
                      </ReferenceField>
                    </Labeled>
                  </div>
                  <div>
                    <Labeled label="Attached trailer(s)">
                      <TextField source="trailerNumbers" emptyText="-" />
                    </Labeled>
                  </div>
                  <div>
                    <Labeled label="Shipping doc number(s)">
                      <TextField
                        source="shippingDocumentNumber"
                        emptyText="-"
                      />
                    </Labeled>
                  </div>
                </div>
                <div className="two-inputs-container">
                  <div>
                    <Labeled label="Total vehicle mileage">
                      <NumberField source="totalVehicleMiles" emptyText="-" />
                    </Labeled>
                  </div>
                  <div>
                    <Labeled label="Total engine hours">
                      <NumberField source="totalEngineHours" emptyText="-" />
                    </Labeled>
                  </div>
                </div>
                <div className="two-inputs-container">
                  <div>
                    <Labeled label="Accumulated vehicle miles">
                      <NumberField
                        source="accumulatedVehicleMiles"
                        emptyText="-"
                      />
                    </Labeled>
                  </div>
                  <div>
                    <Labeled label="Elapsed engine hours">
                      <NumberField source="elapsedEngineHours" emptyText="-" />
                    </Labeled>
                  </div>
                </div>
                <div>
                  <EventSequenceNumberField />
                </div>
                <div>
                  <Labeled label="Note">
                    <TextField source="note" emptyText="-" />
                  </Labeled>
                </div>
              </>
            )}
          </fieldset>
        </Card>
      </RecordContextProvider>
    </div>
  );
};
